class NordsignPdfViewer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.pdfUrl = this.dataset.pdfUrl;
    this.pdfjsUrl = this.dataset.pdfjsUrl;
    this.workerUrl = this.dataset.workerUrl;
    this.canvas = this.querySelector('[data-pdf-canvas]');
    this.status = this.querySelector('[data-pdf-status]');
    this.pageCurrent = this.querySelector('[data-page-current]');
    this.pageTotal = this.querySelector('[data-page-total]');
    this.previousButton = this.querySelector('[data-page-previous]');
    this.nextButton = this.querySelector('[data-page-next]');
    this.zoomOutButton = this.querySelector('[data-zoom-out]');
    this.zoomInButton = this.querySelector('[data-zoom-in]');
    this.zoomValue = this.querySelector('[data-zoom-value]');

    this.pageNumber = 1;
    this.pageCount = 0;
    this.zoom = 1;
    this.rendering = false;
    this.pendingRender = false;

    this.previousButton?.addEventListener('click', () => this.goToPage(this.pageNumber - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.pageNumber + 1));
    this.zoomOutButton?.addEventListener('click', () => this.setZoom(this.zoom - 0.15));
    this.zoomInButton?.addEventListener('click', () => this.setZoom(this.zoom + 0.15));

    const resizeObserver = new ResizeObserver(() => this.queueRender());
    resizeObserver.observe(this);

    this.load();
  }

  async load() {
    if (!this.pdfUrl || !this.pdfjsUrl || !this.workerUrl || !this.canvas) {
      this.setStatus(this.dataset.loadErrorMessage);
      return;
    }

    try {
      this.setStatus(this.dataset.loadingMessage);
      const pdfjsLib = await import(this.pdfjsUrl);
      pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerUrl;
      this.pdfDocument = await pdfjsLib.getDocument({ url: this.pdfUrl }).promise;
      this.pageCount = this.pdfDocument.numPages;
      if (this.pageTotal) this.pageTotal.textContent = this.pageCount;
      this.setStatus('');
      await this.renderPage();
    } catch (error) {
      this.setStatus(this.dataset.interactiveErrorMessage);
      console.error('Nordsign PDF viewer error', error);
    }
  }

  async renderPage() {
    if (!this.pdfDocument || this.rendering) {
      this.pendingRender = Boolean(this.pdfDocument);
      return;
    }

    this.rendering = true;

    try {
      const page = await this.pdfDocument.getPage(this.pageNumber);
      const stage = this.querySelector('.nordsign-catalogue-viewer__stage');
      const availableWidth = Math.max((stage?.clientWidth || 900) - 32, 320);
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = availableWidth / baseViewport.width;
      const viewport = page.getViewport({ scale: fitScale * this.zoom });
      const outputScale = window.devicePixelRatio || 1;
      const context = this.canvas.getContext('2d');

      this.canvas.width = Math.floor(viewport.width * outputScale);
      this.canvas.height = Math.floor(viewport.height * outputScale);
      this.canvas.style.width = `${Math.floor(viewport.width)}px`;
      this.canvas.style.height = `${Math.floor(viewport.height)}px`;

      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      await page.render({ canvasContext: context, viewport }).promise;
      this.updateControls();
    } finally {
      this.rendering = false;
      if (this.pendingRender) {
        this.pendingRender = false;
        this.renderPage();
      }
    }
  }

  queueRender() {
    if (!this.pdfDocument) return;
    this.pendingRender = true;
    window.requestAnimationFrame(() => this.renderPage());
  }

  goToPage(pageNumber) {
    const nextPage = Math.min(Math.max(pageNumber, 1), this.pageCount);
    if (nextPage === this.pageNumber) return;
    this.pageNumber = nextPage;
    this.renderPage();
  }

  setZoom(zoom) {
    this.zoom = Math.min(Math.max(zoom, 0.7), 1.8);
    this.renderPage();
  }

  updateControls() {
    if (this.pageCurrent) this.pageCurrent.textContent = this.pageNumber;
    if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.zoom * 100)}%`;
    if (this.previousButton) this.previousButton.disabled = this.pageNumber <= 1;
    if (this.nextButton) this.nextButton.disabled = this.pageNumber >= this.pageCount;
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = message === '';
  }
}

if (!customElements.get('nordsign-pdf-viewer')) {
  customElements.define('nordsign-pdf-viewer', NordsignPdfViewer);
}
